import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  useTheme,
  styled
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { motion } from 'framer-motion';
import { keyframes } from '@mui/system';

const MotionPaper = motion(Paper);

const floatSquareAnimation = keyframes`
  0% { 
    transform: rotate(45deg) translate(0, 0) scale(1);
    border-color: rgba(63, 160, 255, 0.2);
    box-shadow: 0 0 20px rgba(63, 160, 255, 0.1);
  }
  50% { 
    transform: rotate(45deg) translate(0, -15px) scale(1.1);
    border-color: rgba(63, 160, 255, 0.4);
    box-shadow: 0 0 30px rgba(63, 160, 255, 0.2);
  }
  100% { 
    transform: rotate(45deg) translate(0, 0) scale(1);
    border-color: rgba(63, 160, 255, 0.2);
    box-shadow: 0 0 20px rgba(63, 160, 255, 0.1);
  }
`;

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& *': {
    textShadow: 'none !important',
    filter: 'none !important',
  },
  '& .MuiFormLabel-root, & .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused': {
    textShadow: 'none !important',
    filter: 'none !important',
    WebkitFontSmoothing: 'auto',
    MozOsxFontSmoothing: 'auto',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent',
    borderRadius: '8px',
    transition: 'all 0.3s ease-in-out',
    zIndex: 20,
    position: 'relative',
    backdropFilter: 'none',
    boxShadow: 'none',
    '& fieldset': {
      borderColor: 'rgba(63, 160, 255, 0.4)',
      borderWidth: '1.5px',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(63, 160, 255, 0.6)',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      boxShadow: '0 0 10px rgba(63, 160, 255, 0.2)',
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#000',
    fontSize: '0.95rem',
    fontWeight: 500,
    zIndex: 20,
    textShadow: 'none !important',
    filter: 'none !important',
    WebkitFontSmoothing: 'auto',
    MozOsxFontSmoothing: 'auto',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
    fontSize: '0.95rem',
    padding: '14px',
    position: 'relative',
    zIndex: 20,
    textShadow: 'none',
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.5)',
      opacity: 1,
    },
  },
}));

const ContactForm: React.FC = () => {
  // Debug: Log all environment variables
  console.log('All env vars:', process.env);
  console.log('Google Apps Script URL:', process.env.REACT_APP_GOOGLE_APPS_SCRIPT_URL);
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const [errorMessage, setErrorMessage] = useState<string>('');

  const jsonp = (url: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      const callbackName = 'jsonp_callback_' + Math.round(100000 * Math.random());
      const script = document.createElement('script');
      
      // Create the callback function
      (window as any)[callbackName] = (data: any) => {
        delete (window as any)[callbackName];
        document.body.removeChild(script);
        resolve(data);
      };

      // Add callback to URL
      const separator = url.indexOf('?') === -1 ? '?' : '&';
      script.src = url + separator + 'callback=' + callbackName;
      
      // Handle errors
      script.onerror = () => {
        delete (window as any)[callbackName];
        document.body.removeChild(script);
        reject(new Error('Failed to load script'));
      };

      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      return; // Form validation is handled by HTML5 required attributes
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const scriptUrl = process.env.REACT_APP_GOOGLE_APPS_SCRIPT_URL;
      console.log('Script URL:', scriptUrl);
      
      if (!scriptUrl) {
        throw new Error('Google Apps Script URL is not configured');
      }

      // Validate URL and create URL with parameters
      let url: URL;
      try {
        url = new URL(scriptUrl);
      } catch (error) {
        console.error('Invalid URL:', scriptUrl);
        throw new Error('Invalid Google Apps Script URL');
      }

      // Add form parameters
      url.searchParams.append('method', 'submit');
      url.searchParams.append('name', formData.name);
      url.searchParams.append('email', formData.email);
      url.searchParams.append('company', formData.company || '');
      url.searchParams.append('message', formData.message);

      console.log('Final URL:', url.toString());
      const result = await jsonp(url.toString());
      console.log('JSONP response:', result);

      if (result.result === 'success') {
        setSubmitStatus('success');
        // Clear form
        setFormData({
          name: '',
          email: '',
          company: '',
          message: ''
        });
      } else {
        throw new Error(result.error || 'Submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      component="section"
      sx={{
        pt: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 4, sm: 6, md: 8 },
        mt: { xs: 2, sm: 3, md: 4 },
        mb: { xs: 4, sm: 6, md: 8 },
        position: 'relative',
        overflow: 'visible',
        backgroundColor: 'transparent'
      }}
    >
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          maxWidth: '1200px',
          mx: 'auto',
          px: { xs: 3, sm: 4, md: 6 },
          py: { xs: 4, sm: 5, md: 6 },
          backgroundColor: 'rgba(63, 160, 255, 0.08)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(63, 160, 255, 0.15)',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 8px 30px rgba(63, 160, 255, 0.25)',
            border: '1px solid rgba(63, 160, 255, 0.5)'
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              linear-gradient(45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%),
              linear-gradient(-45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%)
            `,
            backgroundSize: '60px 60px',
            opacity: 0.7,
            zIndex: -1
          }
        }}
      >
        <Box sx={{ display: 'flex', gap: { xs: 4, md: 8 }, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1, width: '100%' }}>
            <Typography
              variant="h4"
              component="h2"
              align="center"
              gutterBottom
              sx={{
                mb: 4,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 70%, #1a4b80 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textTransform: 'uppercase',
                '&::after': {
                  content: '""',
                  display: 'block',
                  width: '60px',
                  height: '4px',
                  backgroundColor: theme.palette.primary.main,
                  margin: '16px auto 0',
                  borderRadius: '2px'
                }
              }}
            >
              HAVE QUESTIONS?
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3
              }}
            >
          <StyledTextField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
          />
          <StyledTextField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            required
          />
          <StyledTextField
            label="Company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            fullWidth
          />
          <StyledTextField
            label="Message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            multiline
            rows={4}
            fullWidth
            required
          />
          {submitStatus === 'success' && (
            <Typography
              variant="subtitle1"
              sx={{
                color: 'success.main',
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              Message sent successfully!
            </Typography>
          )}
          {submitStatus === 'error' && (
            <Typography
              variant="subtitle1"
              sx={{
                color: 'error.main',
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              Failed to send message: {errorMessage || 'Please try again.'}
            </Typography>
          )}
          <Button
            type="submit"
            variant="contained"
            size="large"
            endIcon={<SendIcon />}
            disabled={isSubmitting}
            sx={{
              py: 2,
              px: 4,
              mt: 2,
              borderRadius: 15,
              textTransform: 'none',
              fontSize: { xs: '1.1rem', md: '1.2rem' },
              fontWeight: 600,
              letterSpacing: '0.02em',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '1px solid rgba(63, 160, 255, 0.3)',
              color: '#fff',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 8px 25px rgba(63, 160, 255, 0.4)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)',
                transform: 'translateX(-100%) skewX(-15deg)',
                transition: 'transform 0.7s ease'
              },
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: '0 12px 30px rgba(63, 160, 255, 0.6)',
                transform: 'translateY(-2px)',
                border: '1px solid rgba(63, 160, 255, 0.7)',
                '&::before': {
                  transform: 'translateX(100%) skewX(-15deg)'
                }
              },
              '&:active': {
                transform: 'translateY(1px)',
                boxShadow: '0 4px 15px rgba(63, 160, 255, 0.3)'
              }
            }}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </Button>
            </Box>
          </Box>
          <Box 
            sx={{ 
              flex: 1,
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative'
            }}
          >
            <Box
              component="img"
              src="/contact-image.png"
              alt="Contact Us"
              sx={{
                maxWidth: '100%',
                height: 'auto',
                maxHeight: '400px',
                objectFit: 'contain',
                transform: 'scale(1.1)',
                filter: 'drop-shadow(0 10px 20px rgba(63, 160, 255, 0.2))',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.15)',
                  filter: 'drop-shadow(0 15px 30px rgba(63, 160, 255, 0.3))'
                }
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 40,
            left: 40,
            width: '40px',
            height: '40px',
            border: '1px solid rgba(63, 160, 255, 0.2)',
            borderRadius: '3px',
            zIndex: 1,
            background: 'linear-gradient(135deg, rgba(63, 160, 255, 0.05) 0%, rgba(63, 160, 255, 0.02) 100%)',
            animation: `${floatSquareAnimation} 4s ease-in-out infinite`,
            transition: 'all 0.3s ease-in-out',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -2,
              border: '1px solid rgba(63, 160, 255, 0.15)',
              borderRadius: '4px',
              background: 'linear-gradient(135deg, rgba(63, 160, 255, 0.1) 0%, transparent 100%)'
            }
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            right: 40,
            width: '40px',
            height: '40px',
            border: '1px solid rgba(63, 160, 255, 0.2)',
            borderRadius: '3px',
            zIndex: 1,
            background: 'linear-gradient(135deg, rgba(63, 160, 255, 0.05) 0%, rgba(63, 160, 255, 0.02) 100%)',
            animation: `${floatSquareAnimation} 4s ease-in-out infinite 1s`,
            transition: 'all 0.3s ease-in-out',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -2,
              border: '1px solid rgba(63, 160, 255, 0.15)',
              borderRadius: '4px',
              background: 'linear-gradient(135deg, rgba(63, 160, 255, 0.1) 0%, transparent 100%)'
            }
          }}
        />
      </MotionPaper>
    </Box>
  );
};

export default ContactForm;
